<template>
  <div v-if="!hidden">
    <div
      v-if="savedBlocksStore.syncedSectionsEnabled"
      class="mb-4 flex items-center justify-between"
    >
      <h2 class="font-inter text-sm font-semibold text-gray-50">
        {{ $t("forms.saved_blocks") }}
      </h2>

      <TButton
        data-testid="premade-back-button"
        xs
        @click="savedBlocksStore.savedBlocksOpened = false"
      >
        <template #icon>
          <font-awesome-icon :icon="['fas', 'angle-left']" class="h-[14px]" />
        </template>
        {{ $t("premade.back") }}
      </TButton>
    </div>

    <div v-if="Array.isArray(savedBlocks)">
      <div style="display: flex; justify-content: center">
        <t-note v-if="savedBlocks !== null && savedBlocks.length === 0">{{
          $t("forms.no_saved_blocks")
        }}</t-note>
      </div>

      <div v-for="(savedBlock, blockIndex) in savedBlocks" :key="blockIndex">
        <div v-if="savedBlock" id="saved">
          <VueDraggable
            :key="'savedblocks-' + savedBlock.id"
            :model-value="savedBlocks"
            filter=".not-draggable"
            :prevent-on-filter="true"
            class="space-y-2"
            :item-key="'savedblocks-' + savedBlock.id"
            :animation="150"
            :group="{ name: 'sections', pull: 'clone', put: false }"
            :sort="false"
          >
            <saved-header
              :key="savedBlock.id"
              :ref="
                (el) => {
                  refBlocks[blockIndex] = el;
                }
              "
              data-testid="savedblock-item"
              class="header"
              :clickable="clickable"
              :block="savedBlock"
              :block-index="savedBlock.id || blockIndex"
              @click="$emit('saved-clicked', savedBlock.id || blockIndex)"
              @delete-block="deleteSavedBlock(savedBlock.id || blockIndex)"
              @edit-block="editSavedBlock(savedBlock.id || blockIndex)"
              @mouseover.stop="
                showPreview(
                  blockIndex,
                  savedBlock.definition,
                  savedBlock.translation
                )
              "
              @mouseleave.stop="hidePreview"
              @dragstart="hidePreview"
            ></saved-header>
          </VueDraggable>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        ref="refPreview"
        data-testid="savedblocks-preview"
        class="invisible fixed z-[19999] rounded-md bg-white p-0.5 opacity-0 shadow-[8px_8px_20px_rgba(0,0,0,0.32)] transition-opacity duration-300"
        :style="{ top: `${previewTop}px`, left: `${previewLeft}px` }"
      >
        <iframe
          :srcdoc="preview"
          class="block h-[100vh] min-w-[200px] max-w-[60vw] rounded border-none"
          :style="{
            width: width + 'px',
            zoom: zoom,
          }"
          @load="setPreviewPosition"
        />
      </div>
    </Teleport>
  </div>
</template>
<script lang="ts" setup>
  import SavedHeader from "./_internal/SavedHeader/SavedHeader.vue";
  import { useSavedBlocksStore } from "@/stores/savedBlocksStore";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import usePreview from "@/composables/usePreview";
  import { ISavedBlockDefinition } from "@/schemas/savedBlock/savedBlockSchema";
  import { TopolTemplate } from "@/schemas/templateSchema";
  import { ITranslatableBlockTag } from "@/schemas/template/languageSchema";

  import { TButton, TNote } from "@topol/twig-ui";
  import {
    getEmailPrimaryBlockTranslation,
    emailTranslatableBlockTags,
  } from "@topol/utils";

  import { VueDraggable } from "vue-draggable-plus";
  import { revealHiddenOnDevicePreview } from "@/utils/revealHiddenOnDevicePreview";

  import { ref, computed } from "vue";

  const mjmlStore = useMjmlStore();
  const { getPreview } = usePreview();

  defineProps({
    clickable: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["saved-clicked"]);

  const hidden = ref(false);
  const ableToShowPreview = ref(false);
  const preview = ref<any | null>("");
  const previewIndex = ref<number | undefined>(undefined);
  const previewTop = ref(0);
  const previewLeft = ref(0);
  const hoverTimer = ref<any>(null);
  const refBlocks = ref<any>([]);
  const refPreview = ref();
  const zoom = ref(1);

  const savedBlocksStore = useSavedBlocksStore();

  const savedBlocks = computed(() => {
    return savedBlocksStore.savedBlocks;
  });

  const template = computed(() => {
    return mjmlStore.mjml;
  });

  const width = computed(() => {
    return mjmlStore.getBodyWidth;
  });

  function deleteSavedBlock(id: number) {
    window.$event.emit("deleteSavedBlock", id);
  }

  function editSavedBlock(id: number) {
    window.$event.emit("editSavedBlock", id);
  }

  async function getPreviewHTML(
    definition: ISavedBlockDefinition,
    index: number,
    translation?: any
  ) {
    const previeTemplate: TopolTemplate = JSON.parse(
      JSON.stringify(template.value)
    );
    if (Array.isArray(definition)) {
      previeTemplate.children[0].children = definition;
    } else {
      previeTemplate.children[0].children = [definition];
    }
    if (translation) {
      if (previeTemplate.langs && previeTemplate.langs.length > 0) {
        const primaryLang =
          previeTemplate.langs.find((lang) => lang.primary) ||
          previeTemplate.langs[0];
        if (translation[primaryLang.key]) {
          primaryLang.translations = translation[primaryLang.key];
        } else {
          primaryLang.translations = translation[Object.keys(translation)[0]];
        }
      } else {
        previeTemplate.langs = [
          {
            key: "en",
            primary: true,
            translations: translation[Object.keys(translation)[0]],
          },
        ];
      }
    } else if (previeTemplate.langs && previeTemplate.langs.length > 0) {
      const primaryLang =
        previeTemplate.langs.find((lang) => lang.primary) ||
        previeTemplate.langs[0];
      for (const section of previeTemplate.children[0].children) {
        for (const column of section.children) {
          for (const block of column.children) {
            if (
              emailTranslatableBlockTags.includes(
                block.tagName as ITranslatableBlockTag
              )
            ) {
              primaryLang.translations[block.uid] =
                getEmailPrimaryBlockTranslation(block);
            }
          }
        }
      }
    }
    try {
      const data = await getPreview(JSON.stringify(previeTemplate));
      preview.value = revealHiddenOnDevicePreview(data.data?.html || "");
      if (refBlocks.value[index] && refPreview.value) {
        previewIndex.value = index;
      }
    } catch (error) {
      console.error(error);
      return;
    }
  }

  async function showPreview(i: number, definition: any, translation?: any) {
    clearTimeout(hoverTimer.value);
    ableToShowPreview.value = true;
    hoverTimer.value = setTimeout(() => {
      getPreviewHTML(definition, i, translation);
    }, 200);
  }

  function setPreviewPosition(e: Event) {
    const iframe = e.target as HTMLIFrameElement;
    if (
      !ableToShowPreview.value ||
      !iframe ||
      !iframe.contentWindow ||
      previewIndex.value === undefined ||
      !preview.value
    ) {
      return;
    }
    const i = previewIndex.value;
    if (refBlocks.value[i] && refPreview.value) {
      const winHeight = window.innerHeight;
      const rect = refBlocks.value[i].$el.getBoundingClientRect();
      const rectTop = rect.top + rect.height / 2;
      previewLeft.value = rect.left + rect.width + 5;
      const height = iframe.contentWindow.document.body.scrollHeight;
      let widthZoom = 1;
      let heightZoom = 1;
      if (previewLeft.value + width.value > window.innerWidth) {
        widthZoom = (window.innerWidth - previewLeft.value - 10) / width.value;
      }
      if (height > winHeight * 0.9) {
        heightZoom = (0.9 * winHeight) / height;
      }
      zoom.value = Math.min(widthZoom, heightZoom);
      iframe.style.height = `${height}px`;
      if (rectTop - height / 2 < 0) {
        previewTop.value = 5;
      } else if (rectTop + height / 2 > window.innerHeight) {
        previewTop.value = window.innerHeight - height - 5;
      } else {
        previewTop.value = rectTop - height / 2;
      }
      refPreview.value.classList.remove("invisible");
      refPreview.value.classList.add("!opacity-100");
    }
  }

  function hidePreview() {
    clearTimeout(hoverTimer.value);
    ableToShowPreview.value = false;
    if (refPreview.value) {
      refPreview.value.classList.remove("!opacity-100");
      previewIndex.value = undefined;
      setTimeout(() => {
        if (refPreview.value) {
          refPreview.value.classList.add("invisible");
        }
        preview.value = null;
        zoom.value = 1;
      }, 300);
    }
  }
</script>
